import { Badge, Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useApi,
  type ClientDTO,
  type ClientMailingAddressDTO,
  type Currency,
  type NewDraftOrderRequest,
  type ProductDTO,
} from "../../api";
import { money } from "../../components/cells";

interface LineDraft {
  key: string;
  artworkId: string;
  productId: string;
  quantity: number;
  width: number;
  height: number;
  price: number;
  /** Products this artwork actually exists as; empty until an artwork is picked. */
  products: ProductDTO[];
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  artworkId: "",
  productId: "",
  quantity: 1,
  width: 0,
  height: 0,
  price: 0,
  products: [],
});

/**
 * Creates a Shopify draft order for a wholesale client.
 *
 * Line items are picked artwork-first: choosing an artwork narrows the product
 * list to the sizes that piece is actually made in, and choosing a product
 * fills width, height and the client's negotiated price. A CUSTOM-ratio
 * product is the one case where dimensions stay editable.
 */
export function NewDraftOrderDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();

  const [clientId, setClientId] = useState("");
  const [currency, setCurrency] = useState<Currency>("CAD");
  const [billingId, setBillingId] = useState("");
  const [shippingId, setShippingId] = useState("");
  const [clientOrderNo, setClientOrderNo] = useState("");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [busy, setBusy] = useState(false);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: open,
  });

  const clients = useQuery({
    queryKey: ["client"],
    queryFn: () => api.clients.findAll(),
    enabled: open,
  });

  const artworks = useQuery({
    queryKey: ["artwork", "search"],
    queryFn: () => api.artworkSearch.findAll(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  // Draft orders are always raised on the default storefront.
  const store = (stores.data ?? []).find((entry) => entry.is_default) ?? stores.data?.[0];
  const client: ClientDTO | undefined = (clients.data ?? []).find(
    (entry) => String(entry.id) === clientId,
  );
  const addresses: ClientMailingAddressDTO[] = client?.addresses ?? [];

  useEffect(() => {
    if (!client) return;
    setCurrency(client.currency);
    const preferred = client.addresses?.find((entry) => entry.is_default) ?? client.addresses?.[0];
    setBillingId(preferred ? String(preferred.id) : "");
    setShippingId(preferred ? String(preferred.id) : "");
  }, [client]);

  const artworkOptions = useMemo(
    () =>
      (artworks.data ?? [])
        .map((entry) => ({ value: String(entry.id), label: `${entry.code} - ${entry.title}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [artworks.data],
  );

  const update = (key: string, changes: Partial<LineDraft>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...changes } : line)));

  const pickArtwork = async (key: string, artworkId: string) => {
    update(key, { artworkId, productId: "", width: 0, height: 0, price: 0, products: [] });
    if (!artworkId) return;
    try {
      const entities = await api.productEntities.getByArtwork(Number(artworkId));
      update(key, { products: entities.map((entry) => entry.product).filter(Boolean) });
    } catch {
      toast.error(t("error.generic", "Something went wrong. Please try again."));
    }
  };

  const pickProduct = async (key: string, productId: string) => {
    const line = lines.find((entry) => entry.key === key);
    const product = line?.products.find((entry) => String(entry.id) === productId);
    if (!product) {
      update(key, { productId: "", width: 0, height: 0, price: 0 });
      return;
    }
    update(key, { productId, width: product.width, height: product.height });

    if (!client) return;
    try {
      const prices = await api.clientPriceLines.getByProduct(product.id);
      const line_ = prices[String(client.id)];
      if (line_) update(key, { price: line_.price });
    } catch {
      // No negotiated price simply means the operator types one.
    }
  };

  const submit = async () => {
    if (!store || !client) return;
    const usable = lines.filter((line) => line.artworkId && line.productId && line.quantity > 0);
    if (usable.length === 0) {
      toast.error(
        t("notification.order.minimum-one-product", "You must have at least one product in the order"),
      );
      return;
    }

    const billing = addresses.find((entry) => String(entry.id) === billingId) ?? null;
    const shipping = addresses.find((entry) => String(entry.id) === shippingId) ?? null;

    const request: NewDraftOrderRequest = {
      store: { store },
      client: {
        client,
        billing_address: billing,
        currency,
        order_no: clientOrderNo || null,
        order_date: null,
      },
      customer: null,
      line_items: usable.map((line) => {
        const product = line.products.find((entry) => String(entry.id) === line.productId)!;
        return {
          artwork_id: Number(line.artworkId),
          product,
          quantity: line.quantity,
          width: line.width,
          height: line.height,
          price: line.price,
        };
      }),
      mailing: {
        requires_shipping: true,
        billing_address: billing,
        shipping_address: shipping,
      },
      misc: { note: note || null, tags: tags || null },
    };

    setBusy(true);
    try {
      const created = await api.shopifyOrders.draftCreate(store.id, request);
      toast.success(
        `${t("notification.add-draft-order.success", "Draft order created successfully")} (${created.order_no})`,
      );
      setLines([blankLine()]);
      onCreated();
      onClose();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.add-draft-order.error", "Failed to create draft order"),
      );
    } finally {
      setBusy(false);
    }
  };

  const addressOptions = addresses.map((entry) => ({
    value: String(entry.id),
    label: [entry.company, entry.address1, entry.city, entry.province_code]
      .filter(Boolean)
      .join(", "),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.add-draft-order.title", "Add draft order")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={busy} disabled={!client} onClick={() => void submit()}>
            {t("dialog.confirm.add", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <section>
          <h3 className="mb-2 text-[13px] font-semibold text-ink">
            {t("details.client", "Client")}
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label={t("form.field.store", "Store")}>
              <Input value={store?.name ?? ""} readOnly disabled />
            </Field>
            <Field label={t("form.field.client", "Client")} required>
              <Select
                value={clientId}
                options={(clients.data ?? []).map((entry) => ({
                  value: String(entry.id),
                  label: `${entry.name} - ${entry.code}`,
                }))}
                placeholder={t("form.field.select", "Select")}
                onChange={(event) => setClientId(event.target.value)}
              />
            </Field>
            <Field label={t("form.field.currency", "Currency")} required>
              <Select
                value={currency}
                options={[
                  { value: "CAD", label: "CAD" },
                  { value: "USD", label: "USD" },
                ]}
                onChange={(event) => setCurrency(event.target.value as Currency)}
              />
            </Field>
            <Field label={t("form.field.client-order-no", "Client order no")}>
              <Input
                value={clientOrderNo}
                placeholder={t("form.field.client-order-no.placeholder", "Client order #")}
                onChange={(event) => setClientOrderNo(event.target.value)}
              />
            </Field>
            <Field label={t("form.field.client.billing-address", "Client billing address")} required>
              <Select
                value={billingId}
                options={addressOptions}
                placeholder={t("form.field.select", "Select")}
                onChange={(event) => setBillingId(event.target.value)}
              />
            </Field>
            <Field label={t("form.field.client.shipping-address", "Client shipping address")}>
              <Select
                value={shippingId}
                options={addressOptions}
                placeholder={t("form.field.select", "Select")}
                onChange={(event) => setShippingId(event.target.value)}
              />
            </Field>
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-ink">
              {t("details.products", "Products")}
            </h3>
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus className="size-3.5" />}
              onClick={() => setLines((current) => [...current, blankLine()])}
            >
              {t("button.add.product", "Add product")}
            </Button>
          </div>

          <div className="flex flex-col gap-3">
            {lines.map((line) => {
              const product = line.products.find((entry) => String(entry.id) === line.productId);
              const custom = product?.ratio === "CUSTOM";
              return (
                <div key={line.key} className="rounded-lg border border-line p-3">
                  <div className="grid gap-3 md:grid-cols-6">
                    <Field label={t("form.field.artwork", "Artwork")} required className="md:col-span-2">
                      <Select
                        value={line.artworkId}
                        options={artworkOptions}
                        placeholder={t("form.field.select", "Select")}
                        onChange={(event) => void pickArtwork(line.key, event.target.value)}
                      />
                    </Field>
                    <Field label={t("form.field.product", "Product")} required className="md:col-span-2">
                      <Select
                        value={line.productId}
                        options={line.products.map((entry) => ({
                          value: String(entry.id),
                          label: entry.code,
                        }))}
                        placeholder={t("form.field.select", "Select")}
                        disabled={line.products.length === 0}
                        onChange={(event) => void pickProduct(line.key, event.target.value)}
                      />
                    </Field>
                    <Field label={t("form.field.quantity", "Quantity")}>
                      <Input
                        type="number"
                        min={1}
                        value={line.quantity}
                        onChange={(event) =>
                          update(line.key, { quantity: Number(event.target.value) })
                        }
                      />
                    </Field>
                    <Field label={t("form.field.price", "Price")}>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={line.price}
                        disabled={!line.productId}
                        onChange={(event) => update(line.key, { price: Number(event.target.value) })}
                      />
                    </Field>
                    <Field label={t("form.field.width", "Width")}>
                      <Input
                        type="number"
                        min={0}
                        value={line.width}
                        disabled={!custom}
                        onChange={(event) => update(line.key, { width: Number(event.target.value) })}
                      />
                    </Field>
                    <Field label={t("form.field.height", "Height")}>
                      <Input
                        type="number"
                        min={0}
                        value={line.height}
                        disabled={!custom}
                        onChange={(event) => update(line.key, { height: Number(event.target.value) })}
                      />
                    </Field>
                    <div className="flex items-end md:col-span-4">
                      <Badge variant="neutral" size="sm">
                        {`${t("column.total", "Total")}: ${money(line.price * line.quantity)} ${currency}`}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-auto hover:text-risk"
                        icon={<Trash2 className="size-3.5" />}
                        onClick={() =>
                          setLines((current) =>
                            current.length === 1
                              ? current
                              : current.filter((entry) => entry.key !== line.key),
                          )
                        }
                      >
                        {t("button.cancel", "Remove")}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-[13px] font-semibold text-ink">{t("details.misc", "Misc")}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label={t("form.field.note", "Note")}>
              <Input value={note} onChange={(event) => setNote(event.target.value)} />
            </Field>
            <Field label={t("form.field.tags", "Tags")}>
              <Input
                value={tags}
                placeholder={t("form.field.tags.placeholder", "Tags")}
                onChange={(event) => setTags(event.target.value)}
              />
            </Field>
          </div>
        </section>
      </div>
    </Dialog>
  );
}
